import React, { useCallback, useContext, useState } from 'react';
import { useDescope } from '@descope/nextjs-sdk/client';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PageContext } from '@/context/PageContext';
import { Popover } from 'components/Atoms/Popover/Popover';
import { Icon } from 'components/Atoms/Icon/Icon';
import { LoginModal } from 'components/Auth/LoginModal/LoginModal';
import { setMockSignedIn } from '@/components/Auth/mockResidentSession';
import { useResidentSession } from '@/components/Auth/useResidentSession';
import { buildPublicPathFromSlug } from 'utils/publicUrlRule';
import styles from './Header.module.scss';

export function HeaderUserMenu() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { isAuthenticated, isSessionLoading, isMock, isPreferencesOnly, user } = useResidentSession();
  const sdk = useDescope();
  const router = useRouter();
  const { urlParams, linkHostname } = useContext(PageContext);
  const { t } = useTranslation();

  // Host-aware resident path (ENG-3680): slug-less on a custom domain, slugged on the default host.
  // A hardcoded `/${org}/${locale}/...` breaks navigation on custom hosts (it resolves to a
  // double-slugged route → 404), which is why Communication Preferences / Profile Settings 404'd there.
  const buildResidentPath = useCallback(
    (rest: string[]): string | undefined => {
      if (!urlParams?.organization || !urlParams?.locale) return undefined;
      return buildPublicPathFromSlug([urlParams.organization, urlParams.locale, ...rest], linkHostname);
    },
    [urlParams?.organization, urlParams?.locale, linkHostname],
  );

  const handleLogout = useCallback(() => {
    if (isMock) {
      setMockSignedIn(false);
      return;
    }
    sdk.logout();
  }, [isMock, sdk]);

  const closeUserMenu = () => {
    const expanded = document.querySelector(`.${styles.userMenuButton}[aria-expanded="true"]`) as HTMLElement | null;
    if (expanded) expanded.click();
  };

  const handlePreferences = () => {
    closeUserMenu();
    const path = buildResidentPath(['preferences']);
    if (path) router.push(path);
  };

  const handleProfileClick = () => {
    closeUserMenu();
    const path = buildResidentPath(['profile']);
    if (path) router.push(path);
  };

  const handleSignOutClick = () => {
    closeUserMenu();
    handleLogout();
  };

  if (isSessionLoading) return null;

  if (isAuthenticated) {
    return (
      <Popover
        trigger={
          <button type="button" className={styles.userMenuButton}>
            <Icon name="person" classname={styles.userMenuIcon} />
            <span className={styles.userEmail}>{user?.email || user?.name || 'User'}</span>
            <Icon name="chevron-down" classname={styles.userMenuIcon} />
          </button>
        }
        content={
          <>
            <button type="button" className={styles.userMenuItem} onClick={handlePreferences}>
              {t('auth.comm_preferences', 'Communication Preferences')}
            </button>
            {/* An emailed-link session can't reach the profile endpoints (ENG-3878). */}
            {!isPreferencesOnly && (
              <button type="button" className={styles.userMenuItem} onClick={handleProfileClick}>
                {t('auth.profile', 'Profile Settings')}
              </button>
            )}
            <button type="button" className={styles.userMenuItem} onClick={handleSignOutClick}>
              {t('auth.signOut', 'Sign Out')}
            </button>
          </>
        }
        classname={styles.userMenuPopover}
      />
    );
  }

  return (
    <>
      <button type="button" className={styles.signInLink} onClick={() => setLoginModalOpen(true)}>
        {t('auth.logInSignUp', 'Log in / Sign up')}
      </button>
      <LoginModal open={loginModalOpen} setOpen={setLoginModalOpen} />
    </>
  );
}
